/** The project's dashboard list, shared by every page that links to a dashboard. */
import { getTenantForApiCall } from '@/api/axiosInstance'
import { type Dashboard, type DashboardLayoutType, type DashboardListItem, dashboardApi } from '@/api/dashboardsApi'
import type { DashboardLink } from '@/components/display-section/DisplaySection'
import { queryClient, queryClientDefaults } from '@/lib/queryClient'
import { dashboardKeys } from '@/lib/queryKeys'
import { asList } from '@/utils/asList'
import { type PageError, toPageError } from '@/utils/errorHandler'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

const NO_LINKS: DashboardLink[] = []

/** The backend list query has no ORDER BY, so rows arrive in whatever order Postgres stores them. */
function byIdAscending(a: { id: number }, b: { id: number }): number {
	return a.id - b.id
}

/** id, title and type only — what links, pickers and table columns need. */
// A type alias, not an interface, so it satisfies the `Record<string, unknown>` rows the search bar takes.
export type DashboardSummary = {
	id: number
	title: string
	type?: DashboardLayoutType
	created_by?: string
}

const NO_SUMMARIES: DashboardSummary[] = []

/** Anything the backend can't classify (type absent or unknown) stays undefined rather than guessed. */
function toLayoutType(type: string | null | undefined): DashboardLayoutType | undefined {
	return type === 'device' || type === 'fleet' ? type : undefined
}

/** `name` is the config title; the backend only rewrites it when a request carries one. */
function toDashboardSummary(dashboard: DashboardListItem): DashboardSummary {
	return {
		id: dashboard.id,
		title: dashboard.name ?? dashboard.config?.dashboardMeta?.title ?? '',
		type: toLayoutType(dashboard.type ?? dashboard.config?.dashboardMeta?.type),
		created_by: dashboard.created_by,
	}
}

export function dashboardToSummary(dashboard: Dashboard): DashboardSummary {
	return {
		id: dashboard.id,
		title: dashboard.config?.dashboardMeta?.title ?? '',
		type: toLayoutType(dashboard.config?.dashboardMeta?.type),
		created_by: dashboard.created_by,
	}
}

/** The backend caps a page at 1000. Without configs that is a few tens of KB. */
const SUMMARY_PAGE_SIZE = 1000
/** Pages in flight at once. */
const SUMMARY_PAGE_CONCURRENCY = 4
/** Runaway guard for a backend that never returns a short page. Exceeding it throws. */
const MAX_SUMMARY_PAGES = 100

/** Its own type so the retry below can skip it — re-running every page cannot change the outcome. */
class DashboardListTooLargeError extends Error {}

/**
 * Every dashboard in the project. ShareDashboardDialog expands an `all` permission scope into this
 * list, so a short answer would revoke access to whatever it omitted — this fails instead of
 * returning a partial list. `count` sizes the paging; only a short page ends it.
 */
async function fetchAllDashboardSummaries(): Promise<DashboardSummary[]> {
	// Page 1 alone — almost every project fits in it, and fanning out first would cost extra requests.
	const firstResponse = await dashboardApi.getDashboards(1, SUMMARY_PAGE_SIZE, false)
	const firstPage = asList(firstResponse.data)
	const summaries = firstPage.map(toDashboardSummary)
	if (firstPage.length < SUMMARY_PAGE_SIZE) {
		return summaries
	}

	const hintedLastPage = Math.ceil((firstResponse.count ?? 0) / SUMMARY_PAGE_SIZE)
	let nextPage = 2

	while (nextPage <= MAX_SUMMARY_PAGES) {
		// Never batch past what `count` claims — beyond it, probe one page at a time.
		const lastPageInBatch = Math.min(
			nextPage + SUMMARY_PAGE_CONCURRENCY - 1,
			Math.max(hintedLastPage, nextPage),
			MAX_SUMMARY_PAGES,
		)
		const batch = Array.from({ length: lastPageInBatch - nextPage + 1 }, (_, index) => nextPage + index)
		const responses = await Promise.all(batch.map(page => dashboardApi.getDashboards(page, SUMMARY_PAGE_SIZE, false)))

		let reachedEnd = false
		for (const response of responses) {
			const rows = asList(response.data)
			summaries.push(...rows.map(toDashboardSummary))
			if (rows.length < SUMMARY_PAGE_SIZE) {
				reachedEnd = true
			}
		}
		if (reachedEnd) {
			return summaries
		}
		nextPage += batch.length
	}

	throw new DashboardListTooLargeError(
		`Dashboard list exceeded ${MAX_SUMMARY_PAGES * SUMMARY_PAGE_SIZE} entries; refusing to return a partial list.`,
	)
}

/** The list without configs. Every consumer that only reads a name or a type uses this. */
export function dashboardSummaryListQueryOptions() {
	return queryOptions({
		queryKey: dashboardKeys.summaryList(getTenantForApiCall()),
		queryFn: async () => (await fetchAllDashboardSummaries()).sort(byIdAscending),
		retry: (failureCount: number, error: unknown) => {
			if (error instanceof DashboardListTooLargeError) {
				return false
			}
			return queryClientDefaults.queries.retry(failureCount, error)
		},
	})
}

/** Sidebar/table links for one dashboard type. Derived, never stored — they're a view of the list. */
export function toDashboardLinks(dashboards: DashboardSummary[], type: 'device' | 'fleet'): DashboardLink[] {
	const links = dashboards
		.filter(d => d.type === type)
		.map(d => ({
			id: d.id.toString(),
			label: d.title,
			url: `/dashboards/${d.id}`,
			type,
		}))
	return links.length > 0 ? links : NO_LINKS
}

export interface UseDashboardSummariesResult {
	dashboards: DashboardSummary[]
	deviceDashboardLinks: DashboardLink[]
	fleetDashboardLinks: DashboardLink[]
	isLoading: boolean
	error: PageError | null
	refetch: () => void
}

export function useDashboardSummaries({ enabled = true }: UseDashboardListOptions = {}): UseDashboardSummariesResult {
	const query = useQuery({ ...dashboardSummaryListQueryOptions(), enabled })
	const dashboards = query.data ?? NO_SUMMARIES

	const deviceDashboardLinks = useMemo(() => toDashboardLinks(dashboards, 'device'), [dashboards])
	const fleetDashboardLinks = useMemo(() => toDashboardLinks(dashboards, 'fleet'), [dashboards])

	return {
		dashboards,
		deviceDashboardLinks,
		fleetDashboardLinks,
		isLoading: query.isPending && query.fetchStatus === 'fetching',
		error: query.error ? toPageError(query.error, 'Failed to load dashboards') : null,
		refetch: () => {
			void query.refetch()
		},
	}
}

/** The cached summaries, or undefined when nothing has been fetched yet. Non-reactive. */
export function readDashboardSummaryListCache(): DashboardSummary[] | undefined {
	return queryClient.getQueryData<DashboardSummary[]>(dashboardKeys.summaryList(getTenantForApiCall()))
}

/** Reads the summaries outside React, fetching them once if nothing is cached yet. */
export function ensureDashboardSummaryList(): Promise<DashboardSummary[]> {
	return queryClient.ensureQueryData(dashboardSummaryListQueryOptions())
}

/** Keeps the summaries in step with a create/rename/delete. No-op when nothing is cached. */
export function updateDashboardSummaryListCache(updater: (dashboards: DashboardSummary[]) => DashboardSummary[]): void {
	queryClient.setQueryData<DashboardSummary[]>(dashboardKeys.summaryList(getTenantForApiCall()), current =>
		current ? updater(current) : current,
	)
}

export interface UseDashboardListOptions {
	/** Defaults to true. Pass the condition that used to guard the imperative fetch call. */
	enabled?: boolean
}

/** The last fetched or saved copy of one dashboard, so a revisit or a reload after save paints before the fetch lands. */
export function readDashboardCache(dashboardId: number): Dashboard | undefined {
	return queryClient.getQueryData<Dashboard>(dashboardKeys.detail(getTenantForApiCall(), dashboardId))
}

export function writeDashboardCache(dashboard: Dashboard): void {
	queryClient.setQueryData<Dashboard>(dashboardKeys.detail(getTenantForApiCall(), dashboard.id), dashboard)
}

export function removeDashboardCache(dashboardId: number): void {
	queryClient.removeQueries({ queryKey: dashboardKeys.detail(getTenantForApiCall(), dashboardId), exact: true })
}
